import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import {
    FavoriteType,
    ICorporationResponse,
    ILocationAlertSummaryResponse,
    ISystemAlertSummaryChild,
} from '@kcftech/prometheus-client';
import { InfiniteDataSourceTable } from 'components/infinite_data_source_table/InfiniteDataSourceTable';
import {
    CellContentWrapper,
    ContentWrapper,
    TableExternalLinkIcon,
    TableLink,
} from 'components/infinite_data_source_table/styled';
import { Column } from 'components/infinite_data_source_table/types';
import {
    CorporationAssetsWithAlertsFilter,
    useInfiniteCorporationAssetsWithAlertsDataSource,
} from 'custom_hooks/data_hooks/data_source/corporationAssetsWithAlertsDataSource';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { useFavoritesByType } from 'custom_hooks/data_hooks/favorites/useFavoritesByType';
import {
    Container,
    HeaderContainer,
    TableRow,
    Title as TableTitle,
} from 'pages/Corporation/assets_with_alerts/styled';
import { useCorporationOverviewStore } from 'pages/Corporation/state/CorporationOverviewStoreProvider';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo } from 'react';
import { OverviewStatusLozenge } from 'shared/lozenge/OverviewStatusLozenge';
import {
    getLocationWorkbenchBaseRoute,
    getSystemWorkbenchUrl,
    WorkbenchTab,
} from 'utils/RouteHelper';

const PAGE_LIMIT = 20;

type SystemRowData = ISystemAlertSummaryChild & {
    isSystem: true;
    locationId: string;
};

const isSystemRow = (
    row: ILocationAlertSummaryResponse | SystemRowData,
): row is SystemRowData => {
    return 'System' in row;
};

export type CorporationAssetsWithAlertsWidgetProps = {
    corporation: ICorporationResponse;
    filters?: CorporationOverviewFilters;
};

export type CorporationAssetsWithAlertsWidgetRef = {
    refetch: () => Promise<unknown>;
};

const createColumns = (): Column<ILocationAlertSummaryResponse | SystemRowData>[] => [
    {
        id: 'name',
        header: () => 'Location',
        render: ({
            rowData,
        }: {
            rowData: ILocationAlertSummaryResponse | SystemRowData;
        }) => (
            <TableLink
                to={
                    isSystemRow(rowData)
                        ? getSystemWorkbenchUrl(rowData.System.Id, WorkbenchTab.Trend, {})
                        : getLocationWorkbenchBaseRoute(rowData.Id)
                }
                $isSystem={isSystemRow(rowData)}
                target="_blank"
                rel="noopener noreferrer"
            >
                {isSystemRow(rowData) ? rowData.System.Name : rowData.Name}
                <TableExternalLinkIcon icon={faExternalLinkAlt} aria-hidden="true" />
            </TableLink>
        ),
    },
    {
        id: 'criticalAlarms',
        header: () => <ContentWrapper>Critical Alarms</ContentWrapper>,
        render: ({
            rowData,
        }: {
            rowData: ILocationAlertSummaryResponse | SystemRowData;
        }) => (
            <CellContentWrapper>
                {isSystemRow(rowData)
                    ? rowData.AssetsInCriticalAlarmCount
                    : rowData.TotalAssetsInCriticalAlarmCount}
            </CellContentWrapper>
        ),
        sortField: 'TotalAssetsInCriticalAlarmCount',
        sortable: true,
    },
    {
        id: 'alarms',
        header: () => <ContentWrapper>Alarms</ContentWrapper>,
        render: ({
            rowData,
        }: {
            rowData: ILocationAlertSummaryResponse | SystemRowData;
        }) => (
            <CellContentWrapper>
                {isSystemRow(rowData)
                    ? rowData.AssetsInAlarmCount
                    : rowData.TotalAssetsInAlarmCount}
            </CellContentWrapper>
        ),
        sortField: 'TotalAssetsInAlarmCount',
        sortable: true,
    },
    {
        id: 'warnings',
        header: () => <ContentWrapper>Warnings</ContentWrapper>,
        render: ({
            rowData,
        }: {
            rowData: ILocationAlertSummaryResponse | SystemRowData;
        }) => (
            <CellContentWrapper>
                {isSystemRow(rowData)
                    ? rowData.AssetsInWarningCount
                    : rowData.TotalAssetsInWarningCount}
            </CellContentWrapper>
        ),
        sortField: 'TotalAssetsInWarningCount',
        sortable: true,
    },
];

function CorporationAssetsWithAlertsWidget(
    { corporation, filters }: CorporationAssetsWithAlertsWidgetProps,
    ref: React.ForwardedRef<CorporationAssetsWithAlertsWidgetRef>,
) {
    const { storedSort, setAssetsWithAlertsSort } = useCorporationOverviewStore(
        (state) => ({
            storedSort: state.assetsWithAlertsSort,
            setAssetsWithAlertsSort: state.setAssetsWithAlertsSort,
        }),
    );

    const favorites = useFavoritesByType({
        type: FavoriteType.Location,
        corporationId: corporation.Id,
    });
    const locationIds = useMemo(
        () =>
            filters?.favoritesFilter === 'favorites'
                ? favorites.map((fav) => fav.FavoriteId)
                : undefined,
        [favorites, filters?.favoritesFilter],
    );

    const dataSource = useInfiniteCorporationAssetsWithAlertsDataSource({
        name: 'corporation-assets-with-alerts',
        defaultFilter: {
            corporationId: corporation.Id,
            locationIds,
        },
        defaultSort: storedSort,
        keepPreviousData: true,
        pageLimit: PAGE_LIMIT,
        enabled: true,
    });

    // Wrap setSort to also persist to store
    const handleSort = useCallback(
        (newSort: DataSourceSort[]) => {
            dataSource.setSort(newSort);
            setAssetsWithAlertsSort(newSort);
        },
        [dataSource, setAssetsWithAlertsSort],
    );

    useImperativeHandle(ref, () => ({
        refetch: dataSource.refetch,
    }));

    const columns = useMemo(() => createColumns(), []);

    const getNestedRows = useMemo(
        () =>
            (row: ILocationAlertSummaryResponse): SystemRowData[] => {
                if (!row.Systems.length) {
                    return [];
                }

                const nestedRows = row.Systems.filter(
                    (system) =>
                        system.AssetsInAlarmCount > 0 ||
                        system.AssetsInCriticalAlarmCount > 0 ||
                        system.AssetsInWarningCount > 0,
                ).map((system) => ({
                    ...system,
                    isSystem: true as const,
                    locationId: row.Id,
                }));

                return nestedRows;
            },
        [],
    );

    return (
        <Container>
            <HeaderContainer>
                <TableTitle>Assets with Alerts</TableTitle>
                <OverviewStatusLozenge>PAST 30 DAYS</OverviewStatusLozenge>
            </HeaderContainer>
            <InfiniteDataSourceTable<
                ILocationAlertSummaryResponse,
                CorporationAssetsWithAlertsFilter,
                SystemRowData
            >
                dataSource={dataSource}
                columns={columns}
                rowKey={(row) => (isSystemRow(row) ? row.System.Id : row.Id)}
                sort={dataSource.sort}
                onSort={handleSort}
                getNestedRows={getNestedRows}
                components={{
                    TableRow,
                }}
                noResultsMessage="No assets with alerts found"
            />
        </Container>
    );
}

const memoCorporationAssetsWithAlertsWidget = memo(
    forwardRef<
        CorporationAssetsWithAlertsWidgetRef,
        CorporationAssetsWithAlertsWidgetProps
    >(CorporationAssetsWithAlertsWidget),
);

export { memoCorporationAssetsWithAlertsWidget as CorporationAssetsWithAlertsWidget };
